import clsx from 'clsx';
import React from 'react';
import { createPortal } from 'react-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const Reorderable = ({
  id,
  onReorder,
  items,
  renderItem,
  className,
  usePortal = false,
  isDragDisabled = false,
}) => {
  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    margin: `0 0 0 0`,
    borderRadius: '4px',
    // A dragged row is lifted out of flow (position: fixed), so it needs a
    // background of its own — without one the rest of the list shows through it.
    // The shadow is what makes it read as picked up rather than misplaced.
    ...(isDragging && {
      background: 'var(--background, #fff)',
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    }),
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  function onDragEnd(result) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const reordered = reorder(items, result.source.index, result.destination.index);

    onReorder(reordered);
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={id}>
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef} className={className}>
            {items.map((item, index) => (
              <Draggable
                key={item._id || item.key}
                draggableId={item._id || item.key}
                index={index}
                isDragDisabled={isDragDisabled}
              >
                {(provided, snapshot) => {
                  const child = (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                      {renderItem({
                        dragHandleProps: provided.dragHandleProps,
                        item,
                        index,
                        isDragging: snapshot.isDragging,
                      })}
                    </div>
                  );
                  return snapshot.isDragging && usePortal
                    ? createPortal(child, document.body)
                    : child;
                }}
              </Draggable>
            ))}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

function Wrapper({ children, className }) {
  return (
    <div className={clsx(className, 'grid grid-cols-1 gap-x-4 w-full rounded')}>{children}</div>
  );
}

function Item({ children, isDragging, className, border = true, ...props }) {
  return (
    <div
      {...props}
      className={clsx(className, {
        'border-b border-gray-200 dark:border-border': !isDragging && border,
      })}
    >
      {children}
    </div>
  );
}

Reorderable.Wrapper = Wrapper;
Reorderable.Item = Item;

export default Reorderable;
